import { Component, OnInit, Inject, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';
import { AuthenticationResult, InteractionStatus, PopupRequest, RedirectRequest, EventMessage, EventType } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { NavigationService } from './shared/components/navigation/navigation.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subscription } from 'rxjs';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalModule, MsalService } from '@azure/msal-angular';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { NavigationStart, Router, RouterLink, RouterOutlet } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { HeaderComponent } from './shared/components/header/header.component';
import { ToastrService } from 'ngx-toastr';
import { AdminAuthService } from './shared/services/auth.service';
import { MatCardModule } from '@angular/material/card';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    animations: [
        trigger('slideInOut', [
            state('slideIn', style({
                opacity: 1,
                visibility: 'visible',
                transform: 'translateX(0%)',
            })),
            state('slideOut', style({
                opacity: 0,
                visibility: 'hidden',
                transform: 'translateX(-100%)',
                zIndex: -1,
            })),
            transition('slideIn => slideOut', [style({ zIndex: -1 }), animate('0.2s ease-in')]),
            transition('slideOut => slideIn', [animate('0.2s ease-in')]),
        ]),
    ],
    imports: [CommonModule, MsalModule, RouterOutlet, RouterLink, MatToolbarModule, MatButtonModule, MatMenuModule, HeaderComponent, MatCardModule]
})
export class AppComponent implements OnInit, OnDestroy {
  isIframe = false;
  loginDisplay = false;
  public invertedLoginColor: boolean = true;
  public isAuthorized: boolean = true;
  public isExpanded: boolean = true;
  public isMobileScreen: boolean = false;
  public isMobileNavOpen?: boolean;
  public isSecondaryNavOpen: boolean = false;
  public showMobileSecondaryNav!: Subscription;
  public secondaryNavTitle: string = '';
  public isActiveSecondaryNavUrl?: boolean;
  public disableAnimations: boolean = true;
  public isModalScreen: boolean = false;
  public activeSecondaryNavUrl: string = '';
  public partnerLogo: string = 'https://cdn.personelle.com/platform/images/partnerLogos/personelle/white/logo.svg';
  public partnerName: string = '';
  private renderer: Renderer2;

  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private navService: NavigationService,
    private breakpointObserver: BreakpointObserver,
    private rendererFactory: RendererFactory2,
    private toastr: ToastrService,
    private router: Router,
    private adminAuthService: AdminAuthService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.setThemeColor();
  }

  ngOnInit(): void {
    this.authService.handleRedirectObservable().subscribe();
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal
    this.setLoginDisplay();

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED))
      .subscribe((result: EventMessage) => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });

    this.checkUrl();
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      this.toastr.success('Successfully authenticated');
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
      this.adminAuthService.putAuthToken(activeAccount);

      this.checkUrl();
    }
  }

  checkUrl() {
    const urlToCheck: string = window.location.pathname.split('/')[1];

    this.isAuthorized = this.adminAuthService.isLoggedIn();

    // Check if the url is on the home page
    if (!this.isAuthorized) {
      this.adminAuthService.logOut();
      this.router.navigate(['/dashboard']);
      localStorage.clear();
    }
    else if (this.isAuthorized && window.location.pathname.split('/').length === 2 && urlToCheck === "") {
      this.adminAuthService.setLandingPage();
    }
    else {
      this.adminAuthService.getAuthToken();
    }
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  logout(popup?: boolean) {
    if (popup) {
      this.authService.logoutPopup({
        mainWindowRedirectUri: '/',
      });
    } else {
      this.authService.logoutRedirect();
    }

    this.adminAuthService.logOut();
  }

  public setThemeColor(): void {
    const element: HTMLElement = document.getElementsByTagName('html')[0];
    if (element.className) {
      this.renderer.removeClass(element, element.className);
    }
    this.renderer.addClass(element, 'purple' + '-inverted');
  }

  /**
   * Sets the isMobileScreen property's value based on the resize from the header
   * this property is being used to set classes for elements
   * @param event
   */
  public setMobileView(event: boolean) {
    this.isMobileScreen = event;
  }

  /**
   * Toggle main navigation desktop version to expand or collapse
   * @param event
   */
  public navToggleClicked(event: MouseEvent | KeyboardEvent | TouchEvent): void {
    this.isExpanded = !this.isExpanded;
    this.navService.setDesktopNavToggle(this.isExpanded);
  }

  /**
   * Let the app component now if main nav mobile version is open or not
   * to set overlay/no-scroll class
   * @param event
   */
  public mobileNavOpen(event: boolean) {
    this.isMobileNavOpen = event;
  }

  get title(): string {
    return !this.loginDisplay ? 'Admin' : '';
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
